import type { ArticleReference } from 'domain-model/types/oms';
import qs from 'qs';
import _ from 'lodash';
import getAxiosInstance from '../../config/api';
import type { NextRouter } from 'next/router';

export interface QuerySpecification {
  productCategory?:
    | { iLike: string }
    | { or: [{ eq: string }, { iLike: string }] };
  organizationProductCategory?:
    | { iLike: string }
    | { or: [{ eq: string }, { iLike: string }] };
  [key: string]: object | string | number | boolean | undefined;
}

export interface ListOf<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type ArticleListControllerConfig = {
  showFilterPanel: boolean;
  querySpecification?: QuerySpecification;
  groupBy: string | '';
  sort?: string;
  columns?: string[];
  displayType: 'Card' | 'Table' | 'PurchasingTable';
  router: NextRouter;
};

const mergeProductCategories = (
  configCategory?:
    | { iLike: string }
    | { or: [{ eq: string }, { iLike: string }] },
  controllerCategory?:
    | { iLike: string }
    | { or: [{ eq: string }, { iLike: string }] },
) => {
  if (!configCategory || !controllerCategory) {
    return configCategory || controllerCategory;
  }
  if (
    'iLike' in controllerCategory &&
    'iLike' in configCategory &&
    controllerCategory.iLike.length > configCategory.iLike.length
  ) {
    return controllerCategory;
  }
  if (
    'or' in controllerCategory &&
    'or' in configCategory &&
    controllerCategory.or[0].eq !== configCategory.or[0].eq
  ) {
    return controllerCategory;
  }

  return configCategory;
};

const mergeQuerySpecifications = (
  configQuerySpec?: QuerySpecification,
  controllerQuerySpec?: QuerySpecification,
): QuerySpecification => {
  const mergedQuerySpec: QuerySpecification = { ...configQuerySpec };
  if (controllerQuerySpec === undefined) {
    return mergedQuerySpec;
  }

  Object.keys(controllerQuerySpec).forEach(key => {
    if (
      typeof mergedQuerySpec[key] === 'string' &&
      typeof controllerQuerySpec[key] === 'string'
    ) {
      if (
        (controllerQuerySpec[key] as string).length >
        (mergedQuerySpec[key] as string).length
      ) {
        mergedQuerySpec[key] = controllerQuerySpec[key];
      }
    } else {
      mergedQuerySpec[key] = controllerQuerySpec[key];
    }
  });
  if (mergedQuerySpec.productCategory || controllerQuerySpec.productCategory) {
    mergedQuerySpec.productCategory = mergeProductCategories(
      mergedQuerySpec.productCategory,
      controllerQuerySpec.productCategory,
    );
  }
  return mergedQuerySpec;
};

class ArticleListController {
  private articles: ArticleReference[] = [];
  private groupedArticles: Map<string, ArticleReference[]> = new Map();
  private config: ArticleListControllerConfig = {
    querySpecification: {},
    groupBy: '',
    displayType: 'Card',
    router: {} as NextRouter,
    showFilterPanel: false,
  };
  private loading: boolean = false;
  private filterQuerySpecification: QuerySpecification = {};
  private moreData: boolean = true;
  private error: DOMException | null = null;
  private sort: string = '';
  private page: number = 1;

  private setLoadedController: (
    value: ArticleListController | PromiseLike<ArticleListController>,
  ) => void = x => x;
  private setErrorNextController: (
    value: ArticleListController | PromiseLike<ArticleListController>,
  ) => void = x => x;

  private nextLoadedController: Promise<ArticleListController> =
    new Promise<ArticleListController>((resolve, reject) => {
      this.setLoadedController = resolve;
      this.setErrorNextController = reject;
    });

  private constructor(config: ArticleListControllerConfig) {
    // Initialize the controller with some articles
    this.articles = [];
    this.config = config;
    // Get the values from the query string
    const queryString = config.router.asPath.split('?')[1];
    const query = queryString ? qs.parse(queryString) : {};

    // Set the values from the query string if they exist
    if (query.page) {
      this.page = Number(query.page) - 1; // we remove one as we will increase the page when loading
    }
    if (query.sort) {
      this.sort = query.sort as string;
    }
    if (query.querySpecification) {
      this.filterQuerySpecification = mergeQuerySpecifications(
        config.querySpecification,
        JSON.parse(query.querySpecification as string) as QuerySpecification,
      );
    } else {
      this.filterQuerySpecification = config.querySpecification || {};
    }
  }

  public static create(
    config: ArticleListControllerConfig,
  ): ArticleListController {
    // Create a new instance of the controller
    return new ArticleListController(config);
  }

  public static async fetchDataFromServer(
    controller: ArticleListController,
  ): Promise<ListOf<ArticleReference>> {
    const { config, page, articles } = controller;

    let sort = [config.groupBy, controller.sort].filter(x => x).join(',');

    if (config.displayType !== 'Card' && !sort) {
      sort = 'id';
    }

    const axiosInstance = getAxiosInstance();

    let response: { data: ListOf<ArticleReference> } | null = null;
    let data: ArticleReference[] = [];

    // Loop from page 0 to the current page on the config
    for (let i = 1; i <= page; i++) {
      const currentPage = i;
      const numberOfArticlesPerPage = 20;
      const articlesAlreadyFetched = articles.length;
      if (currentPage * numberOfArticlesPerPage <= articlesAlreadyFetched) {
        continue;
      }
      const currentQuery = qs.stringify({
        query: mergeQuerySpecifications(
          config.querySpecification,
          controller.filterQuerySpecification,
        ),
        page: currentPage,
        sort,
      });
      response = await axiosInstance.get(`/public/articles?${currentQuery}`);

      data = [...data, ...response!.data.rows];

      if (currentPage === response!.data.totalPages) {
        break;
      }
    }

    return {
      rows: data,
      total: response!.data.total,
      page: response!.data.page,
      pageSize: response!.data.pageSize,
      totalPages: response!.data.totalPages,
    };
  }

  private groupArticles(): void {
    // Group the articles by the groupBy key
    this.groupedArticles = new Map();
    this.articles.forEach(article => {
      // @ts-ignore
      const key = article[this.config.groupBy] || '';

      if (!this.groupedArticles.has(String(key))) {
        this.groupedArticles.set(String(key), []);
      }
      this.groupedArticles.get(String(key))?.push(article);
    });
  }

  public withData(result: ListOf<ArticleReference>): ArticleListController {
    // Set the articles to display
    const controller = this.clone();
    controller.articles = result.rows;
    controller.groupArticles();
    return controller;
  }

  public async fetchData(): Promise<void> {
    console.log('fetchData');
    if (this.loading) {
      return;
    }
    const controller = this.clone();

    controller.loading = true;

    this.setLoadedController(controller); // this updates any listener to the loaded setting
    let result: ListOf<ArticleReference> | null = null;
    try {
      result = await ArticleListController.fetchDataFromServer(controller);
    } catch (e) {
      const errorController = controller.clone();
      errorController.error = e as DOMException;
      errorController.loading = false;
      errorController.moreData = false;
      this.setErrorNextController(errorController);
      return;
    }
    const controllerWithData = controller.clone();
    controllerWithData.loading = false;
    controllerWithData.page = controller.page + 1;

    controllerWithData.articles = [
      ...controllerWithData.articles,
      ...result!.rows,
    ];

    controllerWithData.moreData =
      controllerWithData.articles.length < result!.total;

    controllerWithData.groupArticles();
    controllerWithData.sort = controller.sort;
    controller.setLoadedController(controllerWithData); // this updates loading and sets the loaded data

    // Save the values to the query string
    const query = qs.stringify({
      page: controllerWithData.page,
      sort: controllerWithData.sort,
      querySpecification: JSON.stringify(
        mergeQuerySpecifications(
          this.config.querySpecification,
          controllerWithData.filterQuerySpecification,
        ),
      ),
    });

    const currentPath = this.config.router.asPath.split('?')[0];
    const newUrl = `${currentPath}?${query}`;
    this.config.router.replace(newUrl, undefined, {
      scroll: false,
      shallow: true,
    });
  }
  public filterHasValueCallback: ((...args: any[]) => any) | null | undefined;

  public updateQuerySpecification(spec: QuerySpecification): void {
    console.log('A SOUNT EFECT');
    console.log('spec', spec);
    this.filterQuerySpecification = mergeQuerySpecifications(
      this.config.querySpecification,
      spec,
    );
    this.page = 1;
    this.articles = [];
    this.groupedArticles = new Map();
    this.fetchData();
  }

  public getQuerySpecification(): QuerySpecification {
    // Get the articles grouped by the groupBy key
    return this.filterQuerySpecification;
  }

  public withMoreData(moreData: boolean): ArticleListController {
    // Update the moreData flag
    const controller = this.clone();
    controller.moreData = moreData;
    return controller;
  }

  public clone(): ArticleListController {
    // Create a shallow copy of the controller
    const clone = new ArticleListController(this.config);
    clone.articles = this.articles;
    clone.groupedArticles = this.groupedArticles;
    clone.loading = this.loading;
    clone.moreData = this.moreData;
    clone.page = this.page;
    clone.sort = this.sort;
    clone.filterQuerySpecification = this.filterQuerySpecification;
    return clone;
  }

  public setSort(sort: string): void {
    this.sort = sort;
    this.page = 1;
    this.articles = [];
    this.groupedArticles = new Map();
    this.fetchData();
  }

  public toggleSort(sort: string): void {
    let newSort = sort;
    if (this.sort === sort) {
      newSort = this.sort.startsWith('-')
        ? this.sort.slice(1)
        : `-${this.sort}`;
    }
    this.setSort(newSort);
  }

  public getSort(): string {
    // Get the sort order
    return this.sort;
  }
  public updateIfNewQuery(currentConfig: ArticleListControllerConfig): void {
    const routerQuerySpecification = _.toString(
      _.get(currentConfig, 'router.query.querySpecification', ''),
    ).replace('\\"', '"');
    if (
      !_.isEqual(
        routerQuerySpecification,
        JSON.stringify(this.filterQuerySpecification),
      )
    ) {
      this.setLoadedController(new ArticleListController(currentConfig));
    }
  }

  public getData(): ArticleReference[] {
    // Get the articles to display
    return this.articles;
  }

  public getGroupedData(): Map<string, ArticleReference[]> {
    // Get the articles grouped by the groupBy key
    return this.groupedArticles;
  }
  public getDisplayType(): string {
    // Get the articles grouped by the groupBy key
    return this.config.displayType;
  }

  public getGroupingKey(): string {
    // Get the key used for grouping
    return this.config.groupBy;
  }

  public isLoading(): boolean {
    // Check if the controller is currently loading data
    return this.loading;
  }

  public getColumns(): string[] {
    // Get the columns to display
    return this.config.columns || [];
  }
  public showFilterPanel(): boolean {
    return this.config.showFilterPanel;
  }

  public hasMoreData(): boolean {
    // Check if there is more data to load
    return this.moreData;
  }

  public getError(): DOMException | null {
    return this.error;
  }

  public nextController(): Promise<ArticleListController> {
    return this.nextLoadedController;
  }
}

export { ArticleListController };
